import { useCallback, useRef, useState } from 'react';
import type {
  ChatMessage,
  ChatStreamEvent,
  OfferSuggestion,
  PublicResource,
  SourceRef,
  UserProfile,
} from '../../shared/types.ts';
import { readSSE } from '../utils/sse.ts';

/** 画面に表示する1ターン。おじさんの発言には出典と紹介枠がぶら下がる。 */
export type Turn = {
  id: string;
  role: 'user' | 'ojisan';
  text: string;
  sources?: SourceRef[];
  offers?: OfferSuggestion[];
  resources?: PublicResource[];
};

const SESSION_KEY = 'kigyou-ojisan.sessionId';

function getSessionId(): string {
  const existing = localStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem(SESSION_KEY, id);
  return id;
}

export function useChat(profile: UserProfile | null) {
  const [turns, setTurns] = useState<Turn[]>([]);
  /** true = 送信済みでまだ最初の文字が届いていない状態（行灯が息をする） */
  const [thinking, setThinking] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const historyRef = useRef<ChatMessage[]>([]);

  const send = useCallback(
    async (text: string) => {
      const content = text.trim();
      if (!content || streaming) return;

      setError(null);
      const userTurn: Turn = { id: crypto.randomUUID(), role: 'user', text: content };
      const ojisanId = crypto.randomUUID();
      setTurns((prev) => [...prev, userTurn, { id: ojisanId, role: 'ojisan', text: '' }]);
      historyRef.current = [...historyRef.current, { role: 'user', content }];
      setThinking(true);
      setStreaming(true);

      const patch = (update: Partial<Turn>) =>
        setTurns((prev) => prev.map((t) => (t.id === ojisanId ? { ...t, ...update } : t)));

      try {
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            messages: historyRef.current,
            profile: profile ?? undefined,
            sessionId: getSessionId(),
          }),
        });

        if (!response.ok) {
          const detail = (await response.json().catch(() => null)) as { error?: string } | null;
          throw new Error(detail?.error ?? `応答に失敗しました (${response.status})`);
        }

        let accumulated = '';
        for await (const raw of readSSE(response)) {
          const event = raw as ChatStreamEvent;
          switch (event.type) {
            case 'meta':
              patch({ sources: event.sources });
              break;
            case 'delta':
              accumulated += event.text;
              setThinking(false);
              patch({ text: accumulated });
              break;
            case 'offers':
              patch({ offers: event.offers, resources: event.resources });
              break;
            case 'error':
              setError(event.message);
              break;
            case 'done':
              break;
          }
        }

        if (accumulated) {
          historyRef.current = [...historyRef.current, { role: 'assistant', content: accumulated }];
        } else {
          // 応答が空なら履歴を巻き戻し、同じ相談をもう一度送れる状態に戻す
          historyRef.current = historyRef.current.slice(0, -1);
          setTurns((prev) => prev.filter((t) => t.id !== ojisanId));
        }
      } catch (err) {
        historyRef.current = historyRef.current.slice(0, -1);
        setTurns((prev) => prev.filter((t) => t.id !== ojisanId));
        setError(err instanceof Error ? err.message : '通信に失敗しました');
      } finally {
        setThinking(false);
        setStreaming(false);
      }
    },
    [profile, streaming],
  );

  const reset = useCallback(() => {
    historyRef.current = [];
    setTurns([]);
    setError(null);
  }, []);

  return { turns, thinking, streaming, error, send, reset };
}
